import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { EMAIL_REGEX, INDIA_PHONE_REGEX } from '../../../../../../utils/validations';
import {
    addCheckInStaffs,
    getAllCheckInStaff
} from '../../../../../../app/store/slices/event/team/team';
import { errorToast, successToastTopRight } from '../../../../../../utils/toasts/toasts';

export default function useAddCheckInStaff() {
    const dispatch = useDispatch();
    const { eventData } = useSelector(state => state.event);
    const { checkInStaffList } = useSelector(state => state.team);
    const [loading, setLoading] = useState(false)
    const [openAddCheckInStaffForm, setOpenAddCheckInStaffForm] = useState(false);

    const eventId = eventData?.eventId;

    const initialValues = { members: [] };

    // console.log("checkInStaffList -> ", JSON.stringify(checkInStaffList, null, 2))

    const validationSchema = Yup.object({
        members: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().required('Name is required'),
                email: Yup.string()
                    .required('Email is required')
                    .matches(EMAIL_REGEX, 'Email is not valid'),
                phone: Yup.string()
                    .required('Phone number is required')
                    .matches(INDIA_PHONE_REGEX, 'Phone number is not valid')
            })
        )
    });


    /**
     * Effect to fetch existing check-in staff members when the component mounts
     * or when the eventId changes
     * Sets loading state while fetching and handles errors
     */
    useEffect(() => {
        const fetchCheckInStaff = async () => {
            if (eventId) {
                try {
                    setLoading(true);
                    await dispatch(getAllCheckInStaff({ eventId, type: 'staff' })).unwrap();
                } catch (error) {
                    errorToast(error?.message || "Failed to fetch check-in staff");
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchCheckInStaff();
    }, [dispatch]);


    const formik = useFormik({
        initialValues,
        validationSchema,
        validate: (values) => {
            const errors = { members: [] };
            let hasErrors = false;


            /**
             * Custom validation to check for existing staff members by email or phone
             * in the current checkInStaffList with access = true
             */
            if (checkInStaffList?.data?.length > 0) {

                /**
                 * Loop through each member being added and filter the existing staff list
                 */
                values.members.forEach((member, index) => {
                    const existingStaffMatches = checkInStaffList.data.filter(
                        staff =>
                            staff.email.toLowerCase() === member.email.toLowerCase() ||
                            staff.phone === member.phone
                    );


                    /**
                     * If matches are found, check if any of them have access = true
                     * If yes, set the appropriate error messages
                     */
                    existingStaffMatches.forEach((existingStaff) => {
                        if (existingStaff.access === true) {
                            if (!errors.members[index]) errors.members[index] = {};

                            // If a match is found for email
                            if (existingStaff.email.toLowerCase() === member.email.toLowerCase()) {
                                errors.members[index].email = 'Staff member already exists with this email';
                                hasErrors = true;
                            }

                            // If a match is found for phone
                            if (existingStaff.phone === member.phone) {
                                errors.members[index].phone = 'Staff member already exists with this phone';
                                hasErrors = true;
                            }
                        }
                    });
                });
            }

            /**
             * Set the errors object only if there are any errors found
             */
            return hasErrors ? errors : {};
        },
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const payload = {
                    type: 'staff',
                    invites: values.members.map(member => ({
                        email: member.email,
                        name: member.name,
                        phone: member.phone
                    }))
                };

                await dispatch(addCheckInStaffs({ eventId, payload })).unwrap();

                successToastTopRight("Staff added successfully");

                formik.resetForm();
                setOpenAddCheckInStaffForm(false);
            } catch (error) {
                errorToast(error?.data?.message || "Failed to add staff");
            } finally {
                setLoading(false);
            }
        }
    });


    /**
     * Function to add a new staff member form row
     * Adds a new member object to the formik members array
     * If the form is not already open, it opens the form
     */
    function addStaffMember() {
        if (!openAddCheckInStaffForm) setOpenAddCheckInStaffForm(true);
        formik.setFieldValue('members', [
            ...formik.values.members,
            { name: '', email: '', phone: '', accessPermission: 'Available' }
        ]);
    }


    /**
     * Function to delete a staff member form row by index
     * Removes the member object from the formik members array at the specified index
     * If no members are left after deletion, it closes the form
     */
    function deleteStaffMember(index) {
        const updatedMembers = formik.values.members.filter((_, i) => i !== index);
        formik.setFieldValue('members', updatedMembers);

        if (updatedMembers.length === 0) {
            setOpenAddCheckInStaffForm(false);
            return false;
        }
    }


    /**
     * Function to cancel all forms and reset state
     * Resets the formik members array and closes the form
     * Returns false to prevent any default action if used in an event handler
     */
    function cancelAllForm() {
        formik.setFieldValue('members', []);
        formik.resetForm();
        setOpenAddCheckInStaffForm(false);
        return false;
    }


    /**
     * Effect to automatically add a blank staff member form row
     * when the add form is opened and there are no existing staff members
     * Ensures at least one form row is present for user input
     */
    useEffect(() => {
        if (openAddCheckInStaffForm && (!checkInStaffList?.data || checkInStaffList.data.length === 0)) {

            const newMember = {
                name: '',
                email: '',
                phone: '',
                accessPermission: 'Available'
            };

            if (formik.values.members.length === 0) {
                formik.setFieldValue('members', [newMember]);
            }
        }
    }, [openAddCheckInStaffForm]);


    /**
     * Return all necessary states and functions for managing check-in staff
     */
    return {
        formik,
        loading,
        addStaffMember,
        deleteStaffMember,
        cancelAllForm,
        openAddCheckInStaffForm,
        setOpenAddCheckInStaffForm
    };
}